"use client";

import { supabaseBrowser } from "@/lib/supabase/browser";
import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";

type Profile = {
  id: string;
  email: string | null;
  display_name: string | null;
  status: "pending" | "active" | "rejected";
  role: "player_no_fund" | "player_with_fund" | "fund_admin" | "app_admin";
  created_at?: string | null;
};

type ClubRow = {
  id: string;
  name: string;
  kind: string | null;
  created_at: string | null;
};

type NightRow = {
  id: string;
  played_at: string | null;
  created_at?: string | null;
  ended_at?: string | null;
  club_id: string;
  status?: string | null;
};

type PlayerRow = {
  id: string;
  night_id: string;
  kind: string;
  user_id: string | null;
  created_at?: string | null;
  is_bust?: boolean | null;
  busted_at?: string | null;
};

type TxRow = {
  night_id: string;
  player_id: string | null;
  kind: string;
  amount_nok: number | null;
  created_at?: string | null;
};

type MyNightStat = {
  night_id: string;
  club_id: string;
  played_at: string | null;
  net: number;
  buyin: number;
  cashout: number;
  travelFee: number;
  source: "cashgame" | "tournament";
  title?: string | null;
  place?: number | null;
  points?: number;
  rebuys?: number;
};

type TournamentDashRow = {
  id: string;
  club_id: string;
  name: string | null;
  status: string | null;
  buy_in: number | null;
  created_at?: string | null;
  actual_started_at?: string | null;
  actual_finished_at?: string | null;
};

type TournamentPlayerDashRow = {
  id: string;
  tournament_id: string;
  kind: string;
  user_id: string | null;
  created_at?: string | null;
  entry_no?: number | null;
  eliminated_at?: string | null;
};

type TournamentResultDashRow = {
  tournament_player_id: string;
  prize_amount: number | null;
  points_awarded: number | null;
  place: number | null;
  created_at?: string | null;
};

type ChartGranularity = "day" | "week" | "month" | "year";
type YearFilter = number | "all";
type GameModeFilter = "both" | "cashgame" | "tournament";

type WaterBar = {
  label: string;
  from: number;
  to: number;
  delta: number;
};

type MyDashStats = {
  totalNet: number;
  totalBuyin: number;
  totalCashout: number;
  totalTravelPaid: number;
  netAfterTravel: number;

  nightsCount: number;
  cashgameCount: number;
  tournamentCount: number;

  avgNet: number;
  avgBuyin: number;
  avgCashout: number;
  winRatePct: number;
  bestNight: number;
  worstNight: number;
  profitNights: number;
  lossNights: number;

  roiPct: number;
  biggestBuyin: number;
  biggestCashout: number;
  avgTravelPaid: number;
  profitFactor: number;
  bestWinStreak: number;

  totalPlayedHours: number;
  incomePerHour: number;

  totalTournamentPrizes: number;
  totalTournamentPoints: number;
  itmCount: number;
  itmRatePct: number;
  avgFinishPlace: number;
  totalRebuys: number;

  chartBars: WaterBar[];
  recent: MyNightStat[];

  activeNightId: string | null;
  activeNightClubId: string | null;
};

type KpiMetric =
  | "totalNet"
  | "netAfterTravel"
  | "totalBuyin"
  | "totalCashout"
  | "totalTravelPaid"
  | "nightsCount"
  | "cashgameCount"
  | "tournamentCount"
  | "avgNet"
  | "avgBuyin"
  | "avgCashout"
  | "winRatePct"
  | "bestNight"
  | "worstNight"
  | "profitNights"
  | "lossNights"
  | "roiPct"
  | "biggestBuyin"
  | "biggestCashout"
  | "avgTravelPaid"
  | "profitFactor"
  | "bestWinStreak"
  | "totalPlayedHours"
  | "incomePerHour"
  | "totalTournamentPrizes"
  | "totalTournamentPoints"
  | "itmCount"
  | "itmRatePct"
  | "avgFinishPlace"
  | "totalRebuys";

type KpiSlotKey = "kpi1" | "kpi2" | "kpi3" | "kpi4" | "kpi5" | "kpi6";
type KpiPreset = "standard" | "resultat" | "volum" | "avansert" | "custom";

type DashboardPrefs = {
  slots: Record<KpiSlotKey, KpiMetric>;
  granularity: ChartGranularity;
  preset: KpiPreset;
  gameModeFilter: GameModeFilter;
};

function cn(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}

function formatNok(n: number) {
  return new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatPct01(n: number) {
  const v = Math.max(0, Math.min(100, n));
  return `${v.toFixed(0)}%`;
}

function formatSignedPct(n: number) {
  return `${n.toFixed(1)}%`;
}

function formatFactor(n: number) {
  if (!Number.isFinite(n)) return "—";
  if (n >= 999) return "999+";
  return n.toFixed(2);
}

function startOfYearUtcIso(year: number) {
  return new Date(Date.UTC(year, 0, 1, 0, 0, 0)).toISOString();
}

function startOfNextYearUtcIso(year: number) {
  return new Date(Date.UTC(year + 1, 0, 1, 0, 0, 0)).toISOString();
}

function toDateMs(d: string | null | undefined) {
  if (!d) return 0;
  const t = new Date(d).getTime();
  return Number.isFinite(t) ? t : 0;
}

function ymdUtc(dIso: string) {
  const d = new Date(dIso);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function monthLabelUtc(dIso: string) {
  const d = new Date(dIso);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function weekKeyUtc(dIso: string) {
  const d = new Date(dIso);
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);

  const firstThu = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstDayNum + 3);

  const weekNo =
    1 + Math.round((date.getTime() - firstThu.getTime()) / (7 * 24 * 3600 * 1000));

  const y = date.getUTCFullYear();
  return `${y}-W${String(weekNo).padStart(2, "0")}`;
}

function weekKeyToUtcMs(key: string) {
  const m = /^(\d{4})-W(\d{2})$/.exec(key);
  if (!m) return 0;

  const year = Number(m[1]);
  const week = Number(m[2]);
  if (!Number.isFinite(year) || !Number.isFinite(week)) return 0;

  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayNum = (jan4.getUTCDay() + 6) % 7;
  const mondayWeek1 = new Date(Date.UTC(year, 0, 4 - dayNum));
  const monday = new Date(mondayWeek1.getTime() + (week - 1) * 7 * 24 * 3600 * 1000);
  return monday.getTime();
}

function labelToUtcMs(label: string, granularity: ChartGranularity) {
  if (granularity === "day") return toDateMs(label);
  if (granularity === "month") return toDateMs(`${label}-01T00:00:00.000Z`);
  if (granularity === "week") return weekKeyToUtcMs(label);
  return toDateMs(`${label}-01-01T00:00:00.000Z`);
}

function initialsFromName(name: string) {
  const clean = String(name || "").trim();
  if (!clean) return "S";
  const parts = clean.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? parts[1]?.[0] ?? "" : parts[0]?.[1] ?? "";
  return `${first}${second}`.toUpperCase();
}

function defaultPrefs(): DashboardPrefs {
  return {
    slots: {
      kpi1: "totalNet",
      kpi2: "netAfterTravel",
      kpi3: "nightsCount",
      kpi4: "totalBuyin",
      kpi5: "totalCashout",
      kpi6: "totalTravelPaid",
    },
    granularity: "month",
    preset: "standard",
    gameModeFilter: "both",
  };
}

function emptyDashStats(
  activeNightId: string | null = null,
  activeNightClubId: string | null = null
): MyDashStats {
  return {
    totalNet: 0,
    totalBuyin: 0,
    totalCashout: 0,
    totalTravelPaid: 0,
    netAfterTravel: 0,

    nightsCount: 0,
    cashgameCount: 0,
    tournamentCount: 0,

    avgNet: 0,
    avgBuyin: 0,
    avgCashout: 0,
    winRatePct: 0,
    bestNight: 0,
    worstNight: 0,
    profitNights: 0,
    lossNights: 0,

    roiPct: 0,
    biggestBuyin: 0,
    biggestCashout: 0,
    avgTravelPaid: 0,
    profitFactor: 0,
    bestWinStreak: 0,

    totalPlayedHours: 0,
    incomePerHour: 0,

    totalTournamentPrizes: 0,
    totalTournamentPoints: 0,
    itmCount: 0,
    itmRatePct: 0,
    avgFinishPlace: 0,
    totalRebuys: 0,

    chartBars: [],
    recent: [],

    activeNightId,
    activeNightClubId,
  };
}

function metricLabel(m: KpiMetric) {
  switch (m) {
    case "totalNet":
      return "Totalt tap/vinn";
    case "netAfterTravel":
      return "Netto etter reisekasse";
    case "totalBuyin":
      return "Buy-in (sum)";
    case "totalCashout":
      return "Cashout (sum)";
    case "totalTravelPaid":
      return "Betalt reisekasse";
    case "nightsCount":
      return "Antall spill";
    case "cashgameCount":
      return "Cashgames";
    case "tournamentCount":
      return "Turneringer";
    case "avgNet":
      return "Snitt tap/vinn";
    case "avgBuyin":
      return "Snitt buy-in";
    case "avgCashout":
      return "Snitt cashout";
    case "winRatePct":
      return "Winrate";
    case "bestNight":
      return "Beste spill";
    case "worstNight":
      return "Verste spill";
    case "profitNights":
      return "Spill i pluss";
    case "lossNights":
      return "Spill i minus";
    case "roiPct":
      return "ROI %";
    case "biggestBuyin":
      return "Største buy-in";
    case "biggestCashout":
      return "Største cashout";
    case "avgTravelPaid":
      return "Snitt reisekasse";
    case "profitFactor":
      return "Profit factor";
    case "bestWinStreak":
      return "Beste winstreak";
    case "totalPlayedHours":
      return "Spilletid (timer)";
    case "incomePerHour":
      return "Inntekt per time";
    case "totalTournamentPrizes":
      return "Turneringspremier";
    case "totalTournamentPoints":
      return "Turneringspoeng";
    case "itmCount":
      return "ITM";
    case "itmRatePct":
      return "ITM-rate";
    case "avgFinishPlace":
      return "Snitt plassering";
    case "totalRebuys":
      return "Rebuys";
    default:
      return "";
  }
}

function toneClassMoney(v: number) {
  if (v > 0) return "text-emerald-200";
  if (v < 0) return "text-red-200";
  return "text-white";
}

function hiddenMoney() {
  return "****";
}

function hiddenNeutral() {
  return "***";
}

const STEALTH_MODE_STORAGE_KEY = "fiskelandslaget_stealth_mode";

function shortLabel(label: string, granularity: ChartGranularity) {
  if (granularity === "week") return label.slice(5);
  if (granularity === "month") return label.slice(5);
  if (granularity === "day") return label.slice(5);
  return label;
}

export default function DashboardPage() {
  const router = useRouter();

  const BtnBase =
    "rounded-xl px-3 py-3 text-sm font-semibold transition active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500/60";
  const BtnPrimary = cn(
    BtnBase,
    "bg-blue-600 text-white shadow-md hover:bg-blue-500 disabled:opacity-60"
  );
  const BtnGhost = cn(
    BtnBase,
    "border border-slate-700 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
  );
  const Card = "rounded-2xl border border-slate-800 bg-slate-900/40 text-white";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [profile, setProfile] = useState<Profile | null>(null);

  const nowYear = new Date().getFullYear();
  const years = useMemo(
    () => Array.from({ length: 6 }).map((_, i) => nowYear - i),
    [nowYear]
  );
  const [year, setYear] = useState<YearFilter>(nowYear);

  const [clubsForFilter, setClubsForFilter] = useState<ClubRow[]>([]);
  const [dashClubId, setDashClubId] = useState<string>("all");

  const [prefs, setPrefs] = useState<DashboardPrefs>(defaultPrefs());
  const [prefsBusy, setPrefsBusy] = useState(false);

  const [dashBusy, setDashBusy] = useState(false);
  const [dash, setDash] = useState<MyDashStats>(emptyDashStats());

  const [showAllKpis, setShowAllKpis] = useState(false);
  const [stealthMode, setStealthMode] = useState<boolean>(() => {
  if (typeof window === "undefined") return false;

  try {
    return window.localStorage.getItem(STEALTH_MODE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
});
  const [openKpiSlot, setOpenKpiSlot] = useState<KpiSlotKey | null>(null);

  useEffect(() => {
    (async () => {
      setErr(null);

      const { data } = await supabaseBrowser.auth.getSession();
      if (!data.session) {
        router.replace("/login");
        return;
      }

      const user = data.session.user;

      const { data: p, error: pErr } = await supabaseBrowser
        .from("profiles")
        .select("id,email,display_name,status,role,created_at")
        .eq("id", user.id)
        .maybeSingle();

      if (pErr) {
        setErr(pErr.message);
        setLoading(false);
        return;
      }

      if (!p) {
        setErr("Ingen profil funnet. Logg inn på nytt.");
        setLoading(false);
        return;
      }

      setProfile(p as Profile);
      setLoading(false);
    })();
  }, [router]);

  async function signOut() {
    await supabaseBrowser.auth.signOut();
    router.replace("/login");
  }

  async function loadClubsForFilter() {
    const { data: s } = await supabaseBrowser.auth.getSession();
    const uid = s.session?.user?.id;
    if (!uid) {
      setClubsForFilter([]);
      return;
    }

    let clubIds: string[] = [];

    {
      const { data, error } = await supabaseBrowser
        .from("club_members")
        .select("club_id,status")
        .eq("user_id", uid);

      if (!error && data?.length) {
        clubIds = (data as Array<{ club_id: string; status?: string | null }>)
          .filter((r) => (r.status ?? "active") === "active")
          .map((r) => r.club_id)
          .filter(Boolean);
      }
    }

    if (clubIds.length === 0) {
      const { data, error } = await supabaseBrowser
        .from("club_memberships")
        .select("club_id")
        .eq("user_id", uid);

      if (!error && data?.length) {
        clubIds = (data as Array<{ club_id: string }>)
          .map((r) => r.club_id)
          .filter(Boolean);
      }
    }

    const uniqueClubIds = Array.from(new Set(clubIds));
    if (uniqueClubIds.length === 0) {
      setClubsForFilter([]);
      if (dashClubId !== "all") setDashClubId("all");
      return;
    }

    const { data: clubs, error: cErr } = await supabaseBrowser
      .from("clubs")
      .select("id,name,kind,created_at")
      .in("id", uniqueClubIds)
      .order("created_at", { ascending: false });

    if (cErr) {
      setClubsForFilter([]);
      return;
    }

    const rows = (clubs ?? []) as ClubRow[];
    setClubsForFilter(rows);

    const stillExists = rows.some((x) => x.id === dashClubId);
    if (dashClubId !== "all" && !stillExists) setDashClubId("all");
  }

  async function loadDashboardPrefs() {
    if (!profile) return;
    setPrefsBusy(true);

    const { data: s } = await supabaseBrowser.auth.getSession();
    const uid = s.session?.user?.id;
    if (!uid) {
      router.replace("/login");
      return;
    }

    const { data, error } = await supabaseBrowser
      .from("user_dashboard_prefs")
      .select("prefs")
      .eq("user_id", uid)
      .maybeSingle();

    if (!error && (data as any)?.prefs && typeof (data as any).prefs === "object") {
      const raw = (data as any).prefs;
      const def = defaultPrefs();

      setPrefs({
        slots: { ...def.slots, ...(raw.slots ?? {}) },
        granularity: (raw.granularity ?? def.granularity) as ChartGranularity,
        preset: (raw.preset ?? def.preset) as KpiPreset,
        gameModeFilter: (raw.gameModeFilter ?? def.gameModeFilter) as GameModeFilter,
      });
    } else {
      setPrefs(defaultPrefs());
    }

    setPrefsBusy(false);
  }

  async function saveDashboardPrefs(next: DashboardPrefs) {
    if (!profile) return;
    setPrefsBusy(true);

    const { data: s } = await supabaseBrowser.auth.getSession();
    const uid = s.session?.user?.id;
    if (!uid) {
      router.replace("/login");
      return;
    }

    const payload = { prefs: next, updated_at: new Date().toISOString() };
    const { error } = await supabaseBrowser
      .from("user_dashboard_prefs")
      .upsert({ user_id: uid, ...payload } as any, { onConflict: "user_id" });

    setPrefsBusy(false);
    if (!error) setPrefs(next);
  }

  function chunk<T>(arr: T[], size: number) {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  }

  async function getTravelFundMembershipSet(uid: string): Promise<Set<string>> {
    const { data, error } = await supabaseBrowser
      .from("travel_fund_members")
      .select("club_id,is_active")
      .eq("user_id", uid);

    if (error) return new Set<string>();

    const s = new Set<string>();
    ((data ?? []) as Array<{ club_id: string; is_active: boolean }>).forEach((r) => {
      if (r.club_id && (r.is_active ?? true)) s.add(r.club_id);
    });
    return s;
  }

  async function findMyActiveNight(
    uid: string,
    clubIdFilter: string
  ): Promise<{ night_id: string; club_id: string } | null> {
    let q = supabaseBrowser
      .from("poker_nights")
      .select("id,club_id,status,created_at")
      .eq("status", "open")
      .order("created_at", { ascending: false })
      .limit(30);

    if (clubIdFilter !== "all") q = q.eq("club_id", clubIdFilter);

    const { data: nights, error: nErr } = await q;
    if (nErr || !nights?.length) return null;

    const nightIds = (nights as Array<{ id: string; club_id: string }>).map((n) => n.id);

    const { data: players, error: pErr } = await supabaseBrowser
      .from("poker_night_players")
      .select("night_id,user_id,kind")
      .in("night_id", nightIds)
      .eq("kind", "member")
      .eq("user_id", uid);

    if (pErr || !players?.length) return null;

    const setNight = new Set((players as any[]).map((p) => String(p.night_id)));
    const hit = (nights as any[]).find((n) => setNight.has(String(n.id)));
    return hit ? { night_id: String(hit.id), club_id: String(hit.club_id) } : null;
  }

  async function computeMyStats(
    selectedYear: YearFilter,
    selectedClubId: string,
    granularity: ChartGranularity,
    gameModeFilter: GameModeFilter
  ): Promise<MyDashStats> {
    const { data: s } = await supabaseBrowser.auth.getSession();
    const uid = s.session?.user?.id;
    if (!uid) {
      router.replace("/login");
      return emptyDashStats();
    }

    const travelClubSet = await getTravelFundMembershipSet(uid);
    const activeNight = await findMyActiveNight(uid, selectedClubId);

    const allSessions: MyNightStat[] = [];
    let totalPlayedHours = 0;

    if (gameModeFilter === "both" || gameModeFilter === "cashgame") {
      let q = supabaseBrowser
        .from("poker_nights")
        .select("id,played_at,created_at,ended_at,club_id,status")
        .in("status", ["settled", "closed"]);

      if (selectedClubId !== "all") q = q.eq("club_id", selectedClubId);

      if (selectedYear !== "all") {
        const from = startOfYearUtcIso(selectedYear);
        const to = startOfNextYearUtcIso(selectedYear);

        q = q.or(
          `and(played_at.gte.${from},played_at.lt.${to}),and(played_at.is.null,created_at.gte.${from},created_at.lt.${to})`
        );
      }

      const { data: nights, error: nErr } = await q.order("created_at", {
        ascending: true,
      });

      if (!nErr && nights?.length) {
        const nightRows = (nights as NightRow[]).map((r) => ({
          ...r,
          played_at: r.played_at ?? r.created_at ?? null,
        }));

        const nightIds = nightRows.map((x) => x.id).filter(Boolean);
        const playerIdToNightId = new Map<string, string>();
        const playerMeta = new Map<
          string,
          {
            night_id: string;
            created_at: string | null;
            is_bust: boolean;
            busted_at: string | null;
          }
        >();

        for (const batch of chunk(nightIds, 300)) {
          const { data: players, error: pErr } = await supabaseBrowser
            .from("poker_night_players")
            .select("id,night_id,kind,user_id,created_at,is_bust,busted_at")
            .in("night_id", batch)
            .eq("kind", "member")
            .eq("user_id", uid);

          if (pErr) break;

          ((players as PlayerRow[] | null) ?? []).forEach((p) => {
            if (!p?.id || !p?.night_id) return;
            playerIdToNightId.set(p.id, p.night_id);
            playerMeta.set(p.id, {
              night_id: p.night_id,
              created_at: p.created_at ?? null,
              is_bust: Boolean(p.is_bust),
              busted_at: p.busted_at ?? null,
            });
          });
        }

        if (playerIdToNightId.size > 0) {
          const sums = new Map<string, { buyin: number; cashout: number }>();
          const playerCashoutAt = new Map<string, string>();

          for (const id of nightIds) sums.set(id, { buyin: 0, cashout: 0 });

          for (const batch of chunk(nightIds, 200)) {
            const { data: tx, error: txErr } = await supabaseBrowser
              .from("poker_transactions")
              .select("night_id,player_id,kind,amount_nok,created_at")
              .in("night_id", batch);

            if (txErr) break;

            const rows = (tx as TxRow[] | null) ?? [];
            for (const t of rows) {
              const pid = t.player_id ? String(t.player_id) : null;
              if (!pid) continue;
              if (!playerIdToNightId.has(pid)) continue;

              const nightId = String(t.night_id);
              const cur = sums.get(nightId) ?? { buyin: 0, cashout: 0 };

              const amt = Math.round(Number(t.amount_nok ?? 0));
              if (!Number.isFinite(amt) || amt === 0) continue;

              const kind = String(t.kind ?? "").toLowerCase();
              if (kind === "buyin") {
                cur.buyin += amt;
              } else if (kind === "cashout") {
                cur.cashout += amt;
                const createdAt = t.created_at ?? null;
                if (createdAt) {
                  const prev = playerCashoutAt.get(pid);
                  if (!prev || toDateMs(createdAt) < toDateMs(prev)) {
                    playerCashoutAt.set(pid, createdAt);
                  }
                }
              }

              sums.set(nightId, cur);
            }
          }

          const nightIdToPlayedAt = new Map<string, string | null>();
          const nightIdToClubId = new Map<string, string>();
          const nightIdToEndedAt = new Map<string, string | null>();

          nightRows.forEach((n) => {
            nightIdToPlayedAt.set(n.id, n.played_at ?? null);
            nightIdToClubId.set(n.id, n.club_id);
            nightIdToEndedAt.set(n.id, n.ended_at ?? null);
          });

          const cashSessions: MyNightStat[] = nightIds.map((nid) => {
            const s0 = sums.get(nid) ?? { buyin: 0, cashout: 0 };
            const net = s0.cashout - s0.buyin;

            const clubId = nightIdToClubId.get(nid) ?? "";
            const isTravelMemberInThisClub = travelClubSet.has(clubId);
            const travelFee =
              isTravelMemberInThisClub && net > 0 ? Math.round(net * 0.1) : 0;

            const playerEntry = Array.from(playerMeta.entries()).find(
              ([, meta]) => meta.night_id === nid
            );

            if (playerEntry) {
              const [playerId, meta] = playerEntry;
              const startAt = meta.created_at;
              const cashoutAt = playerCashoutAt.get(playerId) ?? null;
              const bustedAt = meta.busted_at ?? null;
              const endedAt = nightIdToEndedAt.get(nid) ?? null;

              const candidateEnds = [cashoutAt, bustedAt, endedAt]
                .filter(Boolean)
                .map((x) => String(x));

              const startMs = toDateMs(startAt);
              const endMs = candidateEnds.length
                ? Math.min(...candidateEnds.map((x) => toDateMs(x)).filter((x) => x > 0))
                : 0;

              if (startMs > 0 && endMs > startMs) {
                totalPlayedHours += (endMs - startMs) / (1000 * 60 * 60);
              }
            }

            return {
              night_id: nid,
              club_id: clubId,
              played_at: nightIdToPlayedAt.get(nid) ?? null,
              net,
              buyin: s0.buyin,
              cashout: s0.cashout,
              travelFee,
              source: "cashgame",
              title: "Cashgame",
              place: null,
              points: 0,
              rebuys: 0,
            };
          });

          allSessions.push(...cashSessions);
        }
      }
    }

    if (gameModeFilter === "both" || gameModeFilter === "tournament") {
      let tq = supabaseBrowser
        .from("tournaments")
        .select(
          "id,club_id,name,status,buy_in,created_at,actual_started_at,actual_finished_at"
        )
        .eq("status", "finished");

      if (selectedClubId !== "all") tq = tq.eq("club_id", selectedClubId);

      if (selectedYear !== "all") {
        const from = startOfYearUtcIso(selectedYear);
        const to = startOfNextYearUtcIso(selectedYear);

        tq = tq.or(
          `and(actual_started_at.gte.${from},actual_started_at.lt.${to}),and(actual_started_at.is.null,created_at.gte.${from},created_at.lt.${to})`
        );
      }

      const { data: tournaments, error: tErr } = await tq.order("created_at", {
        ascending: true,
      });

      if (!tErr && tournaments?.length) {
        const tournamentRows = (tournaments ?? []) as TournamentDashRow[];
        const tournamentIds = tournamentRows.map((x) => x.id).filter(Boolean);

        const tournamentMap = new Map<string, TournamentDashRow>();
        tournamentRows.forEach((t) => tournamentMap.set(t.id, t));

        const playerRows: TournamentPlayerDashRow[] = [];

        for (const batch of chunk(tournamentIds, 300)) {
          const { data, error } = await supabaseBrowser
            .from("tournament_players")
            .select("id,tournament_id,kind,user_id,created_at,entry_no,eliminated_at")
            .in("tournament_id", batch)
            .eq("kind", "member")
            .eq("user_id", uid);

          if (error) break;
          playerRows.push(...(((data as TournamentPlayerDashRow[] | null) ?? [])));
        }

        if (playerRows.length > 0) {
          const playerIds = playerRows.map((x) => x.id);
          const resultMap = new Map<string, TournamentResultDashRow>();

          for (const batch of chunk(playerIds, 300)) {
            const { data, error } = await supabaseBrowser
              .from("tournament_results")
              .select("tournament_player_id,prize_amount,points_awarded,place,created_at")
              .in("tournament_player_id", batch);

            if (error) break;

            (((data as TournamentResultDashRow[] | null) ?? [])).forEach((r) => {
              resultMap.set(r.tournament_player_id, r);
            });
          }

          const tournamentSessions: MyNightStat[] = playerRows
            .map((tp) => {
              const tournament = tournamentMap.get(tp.tournament_id);
              if (!tournament) return null;

              const result = resultMap.get(tp.id);
              const entryNo = Number(tp.entry_no ?? 1);
              const buyin = Math.round(Number(tournament.buy_in ?? 0)) * Math.max(1, entryNo);
              const cashout = Math.round(Number(result?.prize_amount ?? 0));
              const net = cashout - buyin;
              const clubId = tournament.club_id ?? "";
              const isTravelMemberInThisClub = travelClubSet.has(clubId);
              const travelFee =
                isTravelMemberInThisClub && net > 0 ? Math.round(net * 0.1) : 0;

              const startAt =
                tp.created_at ??
                tournament.actual_started_at ??
                tournament.created_at ??
                null;

              const endAt =
                tp.eliminated_at ??
                tournament.actual_finished_at ??
                result?.created_at ??
                null;

              const startMs = toDateMs(startAt);
              const endMs = toDateMs(endAt);

              if (startMs > 0 && endMs > startMs) {
                totalPlayedHours += (endMs - startMs) / (1000 * 60 * 60);
              }

              return {
                night_id: tournament.id,
                club_id: clubId,
                played_at: tournament.actual_started_at ?? tournament.created_at ?? null,
                net,
                buyin,
                cashout,
                travelFee,
                source: "tournament",
                title: tournament.name ?? "Turnering",
                place: Number(result?.place ?? 0) || null,
                points: Number(result?.points_awarded ?? 0),
                rebuys: Math.max(0, entryNo - 1),
              };
            })
            .filter(Boolean) as MyNightStat[];

          allSessions.push(...tournamentSessions);
        }
      }
    }

    if (allSessions.length === 0) {
      return emptyDashStats(activeNight?.night_id ?? null, activeNight?.club_id ?? null);
    }

    allSessions.sort((a, b) => toDateMs(a.played_at) - toDateMs(b.played_at));

    const nightsCount = allSessions.length;
    const cashgameCount = allSessions.filter((x) => x.source === "cashgame").length;
    const tournamentCount = allSessions.filter((x) => x.source === "tournament").length;

    const totalBuyin = allSessions.reduce((acc, x) => acc + x.buyin, 0);
    const totalCashout = allSessions.reduce((acc, x) => acc + x.cashout, 0);
    const totalNet = allSessions.reduce((acc, x) => acc + x.net, 0);
    const totalTravelPaid = allSessions.reduce((acc, x) => acc + x.travelFee, 0);
    const netAfterTravel = totalNet - totalTravelPaid;

    const profitNights = allSessions.filter((x) => x.net > 0).length;
    const lossNights = allSessions.filter((x) => x.net < 0).length;
    const winRatePct = nightsCount > 0 ? (profitNights / nightsCount) * 100 : 0;

    const bestNight = nightsCount ? Math.max(...allSessions.map((x) => x.net)) : 0;
    const worstNight = nightsCount ? Math.min(...allSessions.map((x) => x.net)) : 0;

    const avgNet = nightsCount ? Math.round(totalNet / nightsCount) : 0;
    const avgBuyin = nightsCount ? Math.round(totalBuyin / nightsCount) : 0;
    const avgCashout = nightsCount ? Math.round(totalCashout / nightsCount) : 0;
    const avgTravelPaid = nightsCount ? Math.round(totalTravelPaid / nightsCount) : 0;

    const roiPct = totalBuyin > 0 ? (totalNet / totalBuyin) * 100 : 0;
    const incomePerHour = totalPlayedHours > 0 ? totalNet / totalPlayedHours : 0;

    const biggestBuyin = nightsCount ? Math.max(...allSessions.map((x) => x.buyin)) : 0;
    const biggestCashout = nightsCount ? Math.max(...allSessions.map((x) => x.cashout)) : 0;

    const positiveNetSum = allSessions
      .filter((x) => x.net > 0)
      .reduce((acc, x) => acc + x.net, 0);

    const negativeNetAbs = Math.abs(
      allSessions.filter((x) => x.net < 0).reduce((acc, x) => acc + x.net, 0)
    );

    const profitFactor =
      negativeNetAbs > 0 ? positiveNetSum / negativeNetAbs : positiveNetSum > 0 ? 999 : 0;

    let streak = 0;
    let bestWinStreak = 0;
    for (const x of allSessions) {
      if (x.net > 0) {
        streak += 1;
        if (streak > bestWinStreak) bestWinStreak = streak;
      } else {
        streak = 0;
      }
    }

    const tournamentSessions = allSessions.filter((x) => x.source === "tournament");
    const totalTournamentPrizes = tournamentSessions.reduce((acc, x) => acc + x.cashout, 0);
    const totalTournamentPoints = tournamentSessions.reduce(
      (acc, x) => acc + Number(x.points ?? 0),
      0
    );
    const itmCount = tournamentSessions.filter((x) => x.cashout > 0).length;
    const itmRatePct = tournamentCount > 0 ? (itmCount / tournamentCount) * 100 : 0;

    const finishPlaces = tournamentSessions
      .map((x) => Number(x.place ?? 0))
      .filter((x) => x > 0);

    const avgFinishPlace =
      finishPlaces.length > 0
        ? finishPlaces.reduce((a, b) => a + b, 0) / finishPlaces.length
        : 0;

    const totalRebuys = tournamentSessions.reduce(
      (acc, x) => acc + Number(x.rebuys ?? 0),
      0
    );

    const bucket = new Map<string, number>();
    const labelOf = (iso: string) => {
      switch (granularity) {
        case "day":
          return ymdUtc(iso);
        case "week":
          return weekKeyUtc(iso);
        case "month":
          return monthLabelUtc(iso);
        case "year":
        default:
          return String(new Date(iso).getUTCFullYear());
      }
    };

    for (const x of allSessions) {
      if (!x.played_at) continue;
      const k = labelOf(x.played_at);
      bucket.set(k, (bucket.get(k) ?? 0) + x.net);
    }

    const keys = Array.from(bucket.keys());
    keys.sort((a, b) => labelToUtcMs(a, granularity) - labelToUtcMs(b, granularity));

    const chartBars: WaterBar[] = [];
    let running = 0;
    for (const k of keys) {
      const delta = bucket.get(k) ?? 0;
      const fromV = running;
      const toV = running + delta;
      chartBars.push({ label: k, from: fromV, to: toV, delta });
      running = toV;
    }

    const recent = [...allSessions]
      .sort((a, b) => toDateMs(b.played_at) - toDateMs(a.played_at))
      .slice(0, 8);

    return {
      totalNet,
      totalBuyin,
      totalCashout,
      totalTravelPaid,
      netAfterTravel,

      nightsCount,
      cashgameCount,
      tournamentCount,

      avgNet,
      avgBuyin,
      avgCashout,
      winRatePct,
      bestNight,
      worstNight,
      profitNights,
      lossNights,

      roiPct,
      biggestBuyin,
      biggestCashout,
      avgTravelPaid,
      profitFactor,
      bestWinStreak,

      totalPlayedHours,
      incomePerHour,

      totalTournamentPrizes,
      totalTournamentPoints,
      itmCount,
      itmRatePct,
      avgFinishPlace,
      totalRebuys,

      chartBars,
      recent,

      activeNightId: activeNight?.night_id ?? null,
      activeNightClubId: activeNight?.club_id ?? null,
    };
  }

  async function loadDashboard() {
    if (!profile || profile.status !== "active") return;
    setDashBusy(true);
    setErr(null);

    const stats = await computeMyStats(
      year,
      dashClubId,
      prefs.granularity,
      prefs.gameModeFilter
    );

    setDash(stats);
    setDashBusy(false);
  }

  useEffect(() => {
    if (profile?.status === "active") {
      void loadClubsForFilter();
      void loadDashboardPrefs();
    }
  }, [profile?.status]);

useEffect(() => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      STEALTH_MODE_STORAGE_KEY,
      stealthMode ? "1" : "0"
    );
  } catch {}
}, [stealthMode]);

useEffect(() => {
  if (typeof window === "undefined") return;

  const onStorage = (e: StorageEvent) => {
    if (e.key !== STEALTH_MODE_STORAGE_KEY) return;
    setStealthMode(e.newValue === "1");
  };

  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}, []);

  useEffect(() => {
    if (profile?.status === "active") void loadDashboard();
  }, [profile?.status, year, dashClubId, prefs.granularity, prefs.gameModeFilter]);

  const ALL_METRICS: KpiMetric[] = [
    "totalNet",
    "netAfterTravel",
    "nightsCount",
    "cashgameCount",
    "tournamentCount",
    "profitNights",
    "lossNights",
    "winRatePct",
    "roiPct",
    "incomePerHour",
    "totalPlayedHours",
    "totalBuyin",
    "totalCashout",
    "totalTravelPaid",
    "avgNet",
    "avgBuyin",
    "avgCashout",
    "avgTravelPaid",
    "bestNight",
    "worstNight",
    "biggestBuyin",
    "biggestCashout",
    "profitFactor",
    "bestWinStreak",
    "totalTournamentPrizes",
    "totalTournamentPoints",
    "itmCount",
    "itmRatePct",
    "avgFinishPlace",
    "totalRebuys",
  ];

  function cycle<T>(arr: T[], current: T): T {
    const i = arr.findIndex((x) => x === current);
    return i < 0 ? arr[0]! : arr[(i + 1) % arr.length]!;
  }

  const kpiSlots: Array<{ key: KpiSlotKey; metric: KpiMetric }> = [
    { key: "kpi1", metric: prefs.slots.kpi1 },
    { key: "kpi2", metric: prefs.slots.kpi2 },
    { key: "kpi3", metric: prefs.slots.kpi3 },
    { key: "kpi4", metric: prefs.slots.kpi4 },
    { key: "kpi5", metric: prefs.slots.kpi5 },
    { key: "kpi6", metric: prefs.slots.kpi6 },
  ];

  function metricValue(m: KpiMetric) {
    switch (m) {
      case "totalNet":
        return dash.totalNet;
      case "netAfterTravel":
        return dash.netAfterTravel;
      case "totalBuyin":
        return dash.totalBuyin;
      case "totalCashout":
        return dash.totalCashout;
      case "totalTravelPaid":
        return dash.totalTravelPaid;
      case "nightsCount":
        return dash.nightsCount;
      case "cashgameCount":
        return dash.cashgameCount;
      case "tournamentCount":
        return dash.tournamentCount;
      case "avgNet":
        return dash.avgNet;
      case "avgBuyin":
        return dash.avgBuyin;
      case "avgCashout":
        return dash.avgCashout;
      case "winRatePct":
        return dash.winRatePct;
      case "bestNight":
        return dash.bestNight;
      case "worstNight":
        return dash.worstNight;
      case "profitNights":
        return dash.profitNights;
      case "lossNights":
        return dash.lossNights;
      case "roiPct":
        return dash.roiPct;
      case "biggestBuyin":
        return dash.biggestBuyin;
      case "biggestCashout":
        return dash.biggestCashout;
      case "avgTravelPaid":
        return dash.avgTravelPaid;
      case "profitFactor":
        return dash.profitFactor;
      case "bestWinStreak":
        return dash.bestWinStreak;
      case "totalPlayedHours":
        return dash.totalPlayedHours;
      case "incomePerHour":
        return dash.incomePerHour;
      case "totalTournamentPrizes":
        return dash.totalTournamentPrizes;
      case "totalTournamentPoints":
        return dash.totalTournamentPoints;
      case "itmCount":
        return dash.itmCount;
      case "itmRatePct":
        return dash.itmRatePct;
      case "avgFinishPlace":
        return dash.avgFinishPlace;
      case "totalRebuys":
        return dash.totalRebuys;
      default:
        return 0;
    }
  }

  function metricFormat(m: KpiMetric, v: number) {
    if (stealthMode) {
      if (
        m === "nightsCount" ||
        m === "cashgameCount" ||
        m === "tournamentCount" ||
        m === "profitNights" ||
        m === "lossNights" ||
        m === "bestWinStreak" ||
        m === "itmCount" ||
        m === "totalRebuys"
      ) {
        return hiddenNeutral();
      }
      if (m === "totalPlayedHours") return "***";
      if (m === "winRatePct" || m === "itmRatePct") return "***";
      if (m === "roiPct") return "***";
      if (m === "profitFactor") return "***";
      if (m === "avgFinishPlace") return "***";
      if (m === "incomePerHour") return hiddenMoney();
      if (m === "totalTournamentPoints") return hiddenNeutral();
      return hiddenMoney();
    }

    if (
      m === "nightsCount" ||
      m === "cashgameCount" ||
      m === "tournamentCount" ||
      m === "profitNights" ||
      m === "lossNights" ||
      m === "bestWinStreak" ||
      m === "itmCount" ||
      m === "totalRebuys" ||
      m === "totalTournamentPoints"
    ) {
      return String(Math.round(v));
    }

    if (m === "totalPlayedHours") return `${v.toFixed(1)} t`;
    if (m === "avgFinishPlace") return v > 0 ? v.toFixed(1) : "—";
    if (m === "winRatePct" || m === "itmRatePct") return formatPct01(v);
    if (m === "roiPct") return formatSignedPct(v);
    if (m === "profitFactor") return formatFactor(v);
    if (m === "incomePerHour") return formatNok(v);

    return formatNok(v);
  }

  function metricTone(m: KpiMetric, v: number) {
    if (
      m === "nightsCount" ||
      m === "cashgameCount" ||
      m === "tournamentCount" ||
      m === "profitNights" ||
      m === "lossNights" ||
      m === "bestWinStreak" ||
      m === "totalPlayedHours" ||
      m === "itmCount" ||
      m === "totalRebuys" ||
      m === "totalTournamentPoints"
    ) {
      return "text-white";
    }

    if (m === "avgFinishPlace") {
      if (v > 0 && v <= 3) return "text-emerald-200";
      if (v > 0 && v >= 8) return "text-red-200";
      return "text-white";
    }

    if (m === "winRatePct" || m === "itmRatePct") return "text-white";
    if (m === "roiPct" || m === "incomePerHour") return toneClassMoney(v);

    if (m === "profitFactor") {
      if (v > 1) return "text-emerald-200";
      if (v > 0 && v < 1) return "text-red-200";
      return "text-white";
    }

    return toneClassMoney(v);
  }

  async function setMetricForSlot(slotKey: KpiSlotKey, nextMetric: KpiMetric) {
    const next: DashboardPrefs = {
      ...prefs,
      preset: "custom",
      slots: { ...prefs.slots, [slotKey]: nextMetric },
    };
    setOpenKpiSlot(null);
    await saveDashboardPrefs(next);
  }

  function applyPreset(p: KpiPreset) {
    if (p === "custom") {
      const next: DashboardPrefs = { ...prefs, preset: "custom" };
      void saveDashboardPrefs(next);
      return;
    }

    const presets: Record<Exclude<KpiPreset, "custom">, Record<KpiSlotKey, KpiMetric>> = {
      standard: {
        kpi1: "totalNet",
        kpi2: "netAfterTravel",
        kpi3: "nightsCount",
        kpi4: "totalBuyin",
        kpi5: "totalCashout",
        kpi6: "totalTravelPaid",
      },
      resultat: {
        kpi1: "totalNet",
        kpi2: "avgNet",
        kpi3: "winRatePct",
        kpi4: "roiPct",
        kpi5: "bestNight",
        kpi6: "worstNight",
      },
      volum: {
        kpi1: "nightsCount",
        kpi2: "cashgameCount",
        kpi3: "tournamentCount",
        kpi4: "totalBuyin",
        kpi5: "totalCashout",
        kpi6: "totalRebuys",
      },
      avansert: {
        kpi1: "totalTournamentPoints",
        kpi2: "totalTournamentPrizes",
        kpi3: "itmCount",
        kpi4: "itmRatePct",
        kpi5: "avgFinishPlace",
        kpi6: "profitFactor",
      },
    };

    const next: DashboardPrefs = {
      ...prefs,
      preset: p,
      slots: { ...prefs.slots, ...presets[p] },
    };

    void saveDashboardPrefs(next);
  }

  function KpiCard({
    slotKey,
    metric,
  }: {
    slotKey: KpiSlotKey;
    metric: KpiMetric;
  }) {
    const v = metricValue(metric);

    return (
      <button
        type="button"
        onClick={() => setOpenKpiSlot(slotKey)}
        disabled={prefsBusy || dashBusy}
        className={cn(
          "w-full rounded-xl border border-slate-800 bg-slate-950/40 p-4 text-left",
          "hover:bg-slate-900/60 transition disabled:opacity-60"
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-slate-400 leading-5 break-words">
              {metricLabel(metric)}
            </div>
            <div className={cn("mt-1 text-lg font-semibold leading-6", metricTone(metric, v))}>
              {metricFormat(metric, v)}
            </div>
          </div>

          <div className="shrink-0 rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-300">
            Endre
          </div>
        </div>
      </button>
    );
  }

  function WaterfallChart() {
    const bars = dash.chartBars ?? [];
    if (bars.length === 0) {
      return <div className="mt-2 text-sm text-slate-300">Ingen data i valgt filter.</div>;
    }

    const values = bars.flatMap((b) => [b.from, b.to, 0]);
    const minV = Math.min(...values);
    const maxV = Math.max(...values);
    const range = Math.max(1, maxV - minV);

    const H = 180;
    const padL = 24;
    const padR = 8;
    const padT = 10;
    const padB = 26;

    const baseVisibleW = 320;
    const n = bars.length;
    const gap = 6;
    const forcedWide = n > 30;

    const barW = forcedWide
      ? 14
      : Math.max(6, Math.floor((baseVisibleW - padL - padR - gap * (n - 1)) / n));

    const contentW = forcedWide
      ? Math.max(baseVisibleW, padL + padR + n * barW + (n - 1) * gap + 8)
      : baseVisibleW;

    const innerH = H - padT - padB;

    const xOf = (i: number) => padL + i * (barW + gap);
    const yOf = (v: number) => {
      const t = (v - minV) / range;
      return padT + (1 - t) * innerH;
    };

    const yZero = yOf(0);

    return (
      <div className="mt-3">
        <div className="rounded-xl border border-slate-800 bg-slate-950/30 px-2 py-3 overflow-x-auto">
          <svg
            viewBox={`0 0 ${contentW} ${H}`}
            className="h-[190px]"
            style={{ width: `${contentW}px`, minWidth: "100%" }}
            role="img"
            aria-label="Waterfall"
          >
            <line
              x1={padL}
              x2={contentW - padR}
              y1={yZero}
              y2={yZero}
              stroke="rgba(148,163,184,0.35)"
              strokeWidth={1}
            />

            {bars.map((b, i) => {
              const x = xOf(i);
              const yFrom = yOf(b.from);
              const yTo = yOf(b.to);
              const top = Math.min(yFrom, yTo);
              const height = Math.max(2, Math.abs(yTo - yFrom));
              const isPos = b.delta >= 0;

              const fill = isPos ? "rgba(16,185,129,0.28)" : "rgba(239,68,68,0.28)";
              const stroke = "rgba(30,41,59,0.9)";

              const prev = i > 0 ? bars[i - 1] : null;
              const xPrev = i > 0 ? xOf(i - 1) + barW : 0;
              const yPrevEnd = prev ? yOf(prev.to) : 0;
              const yThisStart = yOf(b.from);

              return (
                <g key={b.label}>
                  {prev ? (
                    <line
                      x1={xPrev}
                      x2={x}
                      y1={yPrevEnd}
                      y2={yThisStart}
                      stroke="rgba(148,163,184,0.35)"
                      strokeWidth={2}
                      strokeLinecap="round"
                    />
                  ) : null}

                  <rect x={x} y={top} width={barW} height={height} fill={fill} stroke={stroke} rx={4} />

                  <text
                    x={x + barW / 2}
                    y={H - 10}
                    textAnchor="middle"
                    fontSize="10"
                    fill="rgba(148,163,184,0.85)"
                  >
                    {shortLabel(b.label, prefs.granularity)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  }

  function Select({
    label,
    value,
    onChange,
    disabled,
    children,
  }: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    disabled?: boolean;
    children: React.ReactNode;
  }) {
    return (
      <label className="block">
        <div className="text-[11px] text-slate-400">{label}</div>
        <select
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "mt-1 w-full rounded-xl border border-slate-800 bg-slate-950/40 px-3 py-2 text-sm text-white",
            "focus:outline-none focus:ring-2 focus:ring-blue-500/60 disabled:opacity-60"
          )}
        >
          {children}
        </select>
      </label>
    );
  }

  const displayName = profile?.display_name || profile?.email || "Spiller";
  const initialBadge = initialsFromName(displayName);

  const clubOptions = [{ id: "all", name: "Alle klubber" }, ...clubsForFilter.map((c) => ({ id: c.id, name: c.name }))];

  const granOptions: ChartGranularity[] = ["day", "week", "month", "year"];
  const granLabel: Record<ChartGranularity, string> = {
    day: "Dager",
    week: "Uker",
    month: "Måneder",
    year: "År",
  };

  if (loading) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-5 bg-slate-950 text-white">
        <div className="text-sm text-slate-300">Laster …</div>
      </main>
    );
  }

  if (err) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col px-5 py-8 bg-slate-950 text-white">
        <div className="rounded-2xl border border-red-900/50 bg-red-950/30 p-5">
          <h1 className="text-lg font-semibold">Feil</h1>
          <p className="mt-2 text-sm text-red-200 break-words whitespace-pre-wrap">{err}</p>
          <button onClick={() => router.refresh()} className={cn(BtnPrimary, "mt-4 w-full")}>
            Prøv igjen
          </button>
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md items-center justify-center px-5 bg-slate-950 text-white">
        <div className="text-sm text-slate-300">Ingen profil funnet.</div>
      </main>
    );
  }

  if (profile.status !== "active") {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col px-5 py-8 bg-slate-950 text-white">
        <div className={cn(Card, "p-5")}>
          <h1 className="text-xl font-semibold">
            {profile.status === "pending" ? "Søknad sendt" : "Ikke godkjent"}
          </h1>
          <p className="mt-2 text-sm text-slate-300">
            Du må være godkjent for å bruke dashboard.
          </p>
          <button type="button" onClick={signOut} className={cn(BtnGhost, "mt-5 w-full")}>
            Logg ut
          </button>
        </div>
      </main>
    );
  }

  return (
    <>
      {openKpiSlot ? (
        <div className="fixed inset-0 z-[120] bg-black/70">
          <div className="absolute inset-0" onClick={() => setOpenKpiSlot(null)} />

          <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-md rounded-t-3xl border border-slate-800 bg-slate-950 shadow-2xl">
            <div className="px-5 pt-3 pb-5">
              <div className="mx-auto h-1.5 w-12 rounded-full bg-slate-700" />

              <div className="mt-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs text-slate-400">Velg KPI</div>
                  <div className="text-base font-semibold text-white">
                    {metricLabel(prefs.slots[openKpiSlot])}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setOpenKpiSlot(null)}
                  className={cn(BtnGhost, "px-3 py-2")}
                >
                  Lukk
                </button>
              </div>

              <div className="mt-4 max-h-[60vh] overflow-y-auto pr-1">
                <div className="space-y-2">
                  {ALL_METRICS.map((m) => {
                    const v = metricValue(m);
                    const isSelected = prefs.slots[openKpiSlot] === m;

                    return (
                      <button
                        key={m}
                        type="button"
                        disabled={prefsBusy || dashBusy}
                        onClick={() => setMetricForSlot(openKpiSlot, m)}
                        className={cn(
                          "w-full rounded-2xl border p-4 text-left transition",
                          isSelected
                            ? "border-blue-500 bg-blue-600/15"
                            : "border-slate-800 bg-slate-950/40 hover:bg-slate-900/60"
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-white">
                              {metricLabel(m)}
                            </div>
                            <div
                              className={cn(
                                "mt-1 text-sm font-medium",
                                stealthMode ? "text-slate-300" : metricTone(m, v)
                              )}
                            >
                              {metricFormat(m, v)}
                            </div>
                          </div>

                          <div className="shrink-0">
                            {isSelected ? (
                              <span className="rounded-lg border border-blue-500 bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white">
                                Valgt
                              </span>
                            ) : (
                              <span className="rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-[11px] text-slate-300">
                                Velg
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <main className="mx-auto flex min-h-dvh max-w-md flex-col px-5 py-6 bg-slate-950 text-white">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setStealthMode((v) => !v)}
              aria-label={stealthMode ? "Slå av stealth mode" : "Slå på stealth mode"}
              title={stealthMode ? "Stealth mode er på" : "Stealth mode er av"}
              className={cn(
                "grid h-11 w-11 place-items-center rounded-full border text-sm font-semibold transition",
                "focus:outline-none focus:ring-2 focus:ring-blue-500/60",
                stealthMode
                  ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
                  : "border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800"
              )}
            >
              {stealthMode ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-5 w-5"
                >
                  <path d="M3 3l18 18" />
                  <path d="M10.58 10.58a2 2 0 0 0 2.83 2.83" />
                  <path d="M9.88 5.09A10.94 10.94 0 0 1 12 5c5.05 0 9.27 3.11 10 7-0.29 1.55-1.19 2.97-2.5 4.14" />
                  <path d="M6.61 6.61C4.62 7.9 3.29 9.81 3 12c0.73 3.89 4.95 7 10 7 1.61 0 3.13-.32 4.5-.9" />
                </svg>
              ) : (
                initialBadge
              )}
            </button>

            <div>
              <div className="text-xs text-slate-400">
                Velkommen {stealthMode ? "• Stealth mode" : ""}
              </div>
              <div className="text-lg font-semibold">{displayName}</div>
            </div>
          </div>

          <button type="button" onClick={signOut} className={cn(BtnGhost, "px-3 py-2")}>
            Logg ut
          </button>
        </header>

        <section className={cn(Card, "mt-5 p-5")}>
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-white">Player stats</h2>

            <button
              type="button"
              onClick={loadDashboard}
              className={cn(BtnGhost, "px-3 py-2")}
              disabled={dashBusy}
            >
              {dashBusy ? "Oppdaterer …" : "Oppdater"}
            </button>
          </div>

          {dash.activeNightId ? (
            <div className="mt-4 rounded-xl border border-slate-800 bg-slate-950/40 p-4">
              <div className="text-sm font-semibold">Aktivt lag</div>
              <button
                type="button"
                onClick={() =>
                  router.push(`/clubs/${dash.activeNightClubId}/nights/${dash.activeNightId}`)
                }
                className={cn(BtnPrimary, "mt-3 w-full")}
              >
                Gå til aktivt lag →
              </button>
            </div>
          ) : null}

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Select
              label="År"
              value={String(year)}
              disabled={dashBusy}
              onChange={(v) => {
                if (v === "all") {
                  setYear("all");
                  return;
                }
                const n = Number(v);
                if (Number.isFinite(n)) setYear(n);
              }}
            >
              <option value="all" className="bg-slate-950 text-white">
                Alle år
              </option>
              {years.map((y) => (
                <option key={y} value={String(y)} className="bg-slate-950 text-white">
                  {y}
                </option>
              ))}
            </Select>

            <Select label="Klubb" value={dashClubId} disabled={dashBusy} onChange={(v) => setDashClubId(v)}>
              {clubOptions.map((c) => (
                <option key={c.id} value={c.id} className="bg-slate-950 text-white">
                  {c.name}
                </option>
              ))}
            </Select>

            <Select
              label="Type"
              value={prefs.gameModeFilter}
              disabled={dashBusy || prefsBusy}
              onChange={(v) =>
                void saveDashboardPrefs({
                  ...prefs,
                  gameModeFilter: v as GameModeFilter,
                })
              }
            >
              <option value="both" className="bg-slate-950 text-white">
                Begge
              </option>
              <option value="cashgame" className="bg-slate-950 text-white">
                Cashgame
              </option>
              <option value="tournament" className="bg-slate-950 text-white">
                Turnering
              </option>
            </Select>
          </div>

          <div className="mt-4">
            <div className="text-[11px] text-slate-400">KPI-sett</div>

            <div className="mt-2 -mx-1 overflow-x-auto pb-1">
              <div className="flex min-w-max gap-2 px-1">
                <button
                  type="button"
                  className={cn(
                    BtnGhost,
                    "shrink-0 px-4 py-2 text-xs whitespace-nowrap",
                    prefs.preset === "standard" && "bg-blue-600 border-blue-500"
                  )}
                  onClick={() => applyPreset("standard")}
                  disabled={prefsBusy || dashBusy}
                >
                  Standard
                </button>

                <button
                  type="button"
                  className={cn(
                    BtnGhost,
                    "shrink-0 px-4 py-2 text-xs whitespace-nowrap",
                    prefs.preset === "resultat" && "bg-blue-600 border-blue-500"
                  )}
                  onClick={() => applyPreset("resultat")}
                  disabled={prefsBusy || dashBusy}
                >
                  Resultat
                </button>

                <button
                  type="button"
                  className={cn(
                    BtnGhost,
                    "shrink-0 px-4 py-2 text-xs whitespace-nowrap",
                    prefs.preset === "volum" && "bg-blue-600 border-blue-500"
                  )}
                  onClick={() => applyPreset("volum")}
                  disabled={prefsBusy || dashBusy}
                >
                  Volum
                </button>

                <button
                  type="button"
                  className={cn(
                    BtnGhost,
                    "shrink-0 px-4 py-2 text-xs whitespace-nowrap",
                    prefs.preset === "avansert" && "bg-blue-600 border-blue-500"
                  )}
                  onClick={() => applyPreset("avansert")}
                  disabled={prefsBusy || dashBusy}
                >
                  Avansert
                </button>

                <button
                  type="button"
                  className={cn(
                    BtnGhost,
                    "shrink-0 px-4 py-2 text-xs whitespace-nowrap",
                    prefs.preset === "custom" && "bg-blue-600 border-blue-500"
                  )}
                  onClick={() => applyPreset("custom")}
                  disabled={prefsBusy || dashBusy}
                >
                  Egendefinert
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            {kpiSlots.map((s) => (
              <KpiCard key={s.key} slotKey={s.key} metric={s.metric} />
            ))}
          </div>

          <div className="mt-5 rounded-xl border border-slate-800 bg-slate-950/30 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Utvikling</div>
                <div className="text-[11px] text-slate-400">Kumulativ (waterfall)</div>
              </div>

              <button
                type="button"
                disabled={prefsBusy}
                onClick={async () => {
                  const nextGran = cycle(granOptions, prefs.granularity);
                  await saveDashboardPrefs({ ...prefs, granularity: nextGran });
                }}
                className={cn(BtnGhost, "px-3 py-2")}
                title="Bytt periode"
              >
                {granLabel[prefs.granularity]}
              </button>
            </div>

            <WaterfallChart />
          </div>

          <div className="mt-5">
            <button
              type="button"
              onClick={() => setShowAllKpis((v) => !v)}
              className={cn(BtnGhost, "w-full")}
            >
              {showAllKpis ? "Skjul alle KPIer" : "Vis alle KPIer"}
            </button>

            {showAllKpis ? (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {ALL_METRICS.map((m) => {
                  const v = metricValue(m);
                  return (
                    <div key={m} className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">
                      <div className="text-xs text-slate-400 leading-5 break-words">
                        {metricLabel(m)}
                      </div>
                      <div
                        className={cn(
                          "mt-1 text-base font-semibold leading-6",
                          stealthMode ? "text-slate-300" : metricTone(m, v)
                        )}
                      >
                        {metricFormat(m, v)}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="mt-6">
            <div className="text-sm font-semibold">Siste spill</div>
            {dash.recent.length === 0 ? (
              <div className="mt-2 text-sm text-slate-300">Ingen data i valgt filter.</div>
            ) : (
              <div className="mt-3 space-y-2">
                {dash.recent.map((x, idx) => (
                  <div
                    key={`${x.source}-${x.night_id}-${x.played_at ?? "nodate"}-${idx}`}
                    className="rounded-xl border border-slate-800 bg-slate-950/40 px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs text-slate-400">
                        {x.played_at
                          ? new Date(x.played_at).toLocaleDateString("nb-NO")
                          : "Ukjent dato"}
                      </div>
                      <div
                        className={cn(
                          "text-sm font-semibold",
                          stealthMode
                            ? "text-slate-300"
                            : x.net >= 0
                            ? "text-emerald-200"
                            : "text-red-200"
                        )}
                      >
                        {stealthMode ? hiddenMoney() : formatNok(x.net)}
                      </div>
                    </div>

                    <div className="mt-1 text-[11px] text-slate-500">
                      {stealthMode ? (
                        x.source === "tournament" ? (
                          "Turnering • Buy-in **** • Premie **** • Poeng ***"
                        ) : (
                          "Cashgame • Buy-in **** • Cashout **** • Reisekasse ****"
                        )
                      ) : x.source === "tournament" ? (
                        `${x.title ?? "Turnering"} • Buy-in ${formatNok(
                          x.buyin
                        )} • Premie ${formatNok(x.cashout)} • Poeng ${Math.round(
                          Number(x.points ?? 0)
                        )}${x.place ? ` • Plass ${x.place}` : ""}${
                          Number(x.rebuys ?? 0) > 0 ? ` • Rebuys ${x.rebuys}` : ""
                        }`
                      ) : (
                        `Cashgame • Buy-in ${formatNok(x.buyin)} • Cashout ${formatNok(
                          x.cashout
                        )} • Reisekasse ${formatNok(x.travelFee)}`
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}